import { useEffect, useState } from "react"
import iconNoData from "assets/images/icons/icon-no-data-component.svg"
import Button from "components/Button/Button"

import { Avatar } from "components/Avatar"
import { ReactComponent as IconHasKey } from "assets/images/icons/icon-checked-build-active.svg"
import { ReactComponent as IconNotHasKey } from "assets/images/icons/icon-active-select-currency.svg"
import { EncryptionKeys } from "controllers/EncryptionController"
import { EncryptedDataWarning } from "components/EncryptedDataWarning"
import {
  getProjectBuildInviteeMiddleware,
  getProjectBuildMiddleware,
  postProjectBuildSendInviteeMiddleware,
} from "pages/project-build/api.services"
import {
  BUILD_INVITEE_STATUS,
  emptyProjectBuildDetail,
  ProjectBuildInvitessDetail,
  ProjectComponentBuildDetail,
} from "pages/project-build/project-build.type"
import LabelStatusBuild from "pages/project-component/molecules/LabelStatusBuild"
import DotCard from "pages/conversations/atoms/DotCard"
import { STATUS_BUILD } from "components/Status/types"
import { umamiTracking } from "helpers/utils"
import { EVENT } from "constants/events"
import { useAddComponentInInvitee } from "hooks/useAddComponentInInvitee"
import { configureStore } from "stores/configureStore"
import { startLoading } from "reducers/loading"
import { STATUS_RESPONSE } from "types"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { MessageBackupType } from "components/FormBackup/type"
import { useBoolean } from "helpers/hooks"
import { SkeletonProjectBuildDetail } from "../skeleton/SkeletonAccessKeyProjectBuild"
import Typography from "components/Typography"
import { FormBackup } from "components/FormBackup/FormBackup"
import { cloneDeep, compact } from "lodash"
import { syncNewVersionComponentInviteeMiddleWare } from "pages/project-component-detail/services"
import { closeModal, openModal } from "reducers/modal"
import { encryptionGCMHelper } from "helpers/encryption-gcm"

interface Props {
  projectBuildIdActive: {
    setValue: (value: string) => void
    value: string
  }
}
export const ProjectBuildInviteeCard = (props: Props) => {
  const { projectBuildIdActive } = props

  const projectEncryptionKeys = JSON.parse(
    localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
  )
  const buildAdditionalInfoEncryptionKeys = JSON.parse(
    localStorage.getItem(EncryptionKeys.buildAdditionalInfoEncryptionKeys) ||
      "{}"
  )
  const currentProjectBuildEncryptionKey =
    buildAdditionalInfoEncryptionKeys[projectBuildIdActive.value]
  const [projectBuild, setProjectBuild] = useState<ProjectComponentBuildDetail>(
    emptyProjectBuildDetail
  )
  const [invitess, setInvitess] = useState<ProjectBuildInvitessDetail[]>([])
  const loadingData = useBoolean(false)

  const {
    syncNewVersionComponentInvitee,
    messageBackups,
    currentMessageBackup,
    errorMessageBackup,
    isLoadingBackupInvitee,
    setMessageBackups,
    setCurrentMessageBackup,
    createNewComponentHistoryByOwner,
  } = useAddComponentInInvitee()

  useEffect(() => {
    if (!projectBuildIdActive.value) {
      setProjectBuild(emptyProjectBuildDetail)
      return
    }
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectBuildIdActive.value])

  const getData = async () => {
    try {
      loadingData.setValue(true)
      setProjectBuild(emptyProjectBuildDetail)
      setInvitess([])
      const dataProjectDetailRes = await getProjectBuildMiddleware(
        projectBuildIdActive.value,
        undefined,
        true
      )
      if (dataProjectDetailRes?.id) {
        const projectKey =
          projectEncryptionKeys[dataProjectDetailRes.project_id]
        const decryptedConversationKeys = await Promise.all(
          (dataProjectDetailRes.conversationKeys || []).map(
            async ({ encryptedKey, conversationCode }) => {
              if (!encryptedKey) return { conversationCode, isDecrypted: false }

              const decrypted = await encryptionGCMHelper.decryptContext(
                projectKey,
                encryptedKey
              )
              const isDecrypted =
                typeof decrypted === "string" &&
                decrypted.length === 64 &&
                decrypted !== encryptedKey

              return { conversationCode, isDecrypted }
            }
          )
        )
        const notDecryptedConversationKeys = decryptedConversationKeys.filter(
          ({ isDecrypted }) => !isDecrypted
        )
        const decryptedBuildKey = await encryptionGCMHelper.decryptContext(
          projectKey,
          dataProjectDetailRes?.buildKey?.encryptedKey
        )

        setProjectBuild({
          ...dataProjectDetailRes,
          notDecryptedConversationKeys,
          decryptedBuildKey,
        })
        await getInvitees(dataProjectDetailRes.id, true)
      }
    } catch (error) {
      setProjectBuild(emptyProjectBuildDetail)
      setInvitess([])
    } finally {
      loadingData.setValue(false)
    }
  }

  const getInvitees = async (idBuild: string, isLoading = false) => {
    const dataInvitees = await getProjectBuildInviteeMiddleware(
      idBuild,
      isLoading
    )
    setInvitess(dataInvitees)
  }

  const handleShareKey =
    (
      emails: string[],
      originInvite: ProjectBuildInvitessDetail,
      reloadData = true,
      isSentMailAll = false
    ) =>
    async () => {
      if (!emails.length) {
        return
      }
      umamiTracking(EVENT.INVITEE.SHARE_KEY)
      const generateDataKeyPairUsers = await syncNewVersionComponentInvitee(
        originInvite.project_build_components,
        projectBuild.project_id,
        originInvite,
        projectBuild.id,
        originInvite.conversation_id,
        emails
      )
      configureStore.dispatch(startLoading())
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          postProjectBuildSendInviteeMiddleware(
            originInvite.id,
            "",
            async (type: STATUS_RESPONSE) => {
              if (type === STATUS_RESPONSE.SUCCESS) {
                toast(
                  <LabelNotificationPage
                    messenger={`${
                      originInvite.conversation_code
                    }: The key has been successfully shared with ${emails.join(
                      ", "
                    )}!`}
                    type="success"
                  />
                )
                if (reloadData) {
                  setCurrentMessageBackup(MessageBackupType.DONE_BACKUP_VERSION)
                  await getInvitees(projectBuild.id)
                  setTimeout(() => {
                    isLoadingBackupInvitee.setValue(false)
                  }, 2000)
                }
              } else {
                toast(
                  <LabelNotificationPage
                    messenger={`The key has been failed shared with ${emails.join(
                      ", "
                    )}!`}
                    type="success"
                  />
                )
              }
              resolve()
            },
            emails,
            generateDataKeyPairUsers,
            isSentMailAll,
            isSentMailAll
          )
        }, 500)
      })
    }

  const handleWarningShareAllKey =
    (emails: string[], originInvite: ProjectBuildInvitessDetail) => () => {
      configureStore.dispatch(
        openModal({
          type: "Delete",
          props: {
            deleteModal: {
              title: (
                <p>
                  Are you sure you want to share all keys in{" "}
                  <span
                    style={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {originInvite.conversation_code}
                  </span>
                  ?
                </p>
              ),
              label: `Warning`,
              content: `This action cannot be undone!`,
              styleTitle: { textAlign: "center" },
              titleButton: `Proceed`,
              colorYellowButton: true,
              onSubmit: async () => {
                closeModal()
                handleShareKey(emails, originInvite)()
              },
            },
          },
        })
      )
    }
  const handleShareAllKeyForBuild =
    (originInvitees: ProjectBuildInvitessDetail[], isSentMailAll: boolean) =>
    async () => {
      configureStore.dispatch(
        openModal({
          type: "Delete",
          props: {
            deleteModal: {
              title: `Are you sure you want to ${
                isSentMailAll
                  ? `share all invitations and keys`
                  : "share all keys"
              }?`,
              label: `Warning`,
              content: `This action cannot be undone!`,
              styleTitle: { textAlign: "center" },
              titleButton: `Proceed`,
              colorYellowButton: true,
              onSubmit: async () => {
                closeModal()
                let newInvitees = cloneDeep(originInvitees)
                const components: {
                  project_component_history_id: string
                  code: string
                  version: string
                  type: number
                  conversation_id: string
                }[] = []
                newInvitees.forEach((invitee) => {
                  if (
                    invitee.project_build_components.length &&
                    invitee.status === BUILD_INVITEE_STATUS.ACTIVATE
                  ) {
                    invitee.project_build_components.forEach((elComponent) => {
                      if (
                        !elComponent.componentShareKey ||
                        !elComponent.project_component_history_share_id ||
                        elComponent.project_component_history_id !==
                          elComponent.project_component_history_latest_id
                      ) {
                        const historyId =
                          elComponent.project_component_history_latest_id ||
                          elComponent.project_component_history_id
                        const componentExisted = components.find(
                          (el) => el.project_component_history_id === historyId
                        )
                        if (!componentExisted) {
                          components.push({
                            project_component_history_id: historyId,
                            code: elComponent.code,
                            version: elComponent.version,
                            type: elComponent.type,
                            conversation_id: invitee.conversation_id,
                          })
                        }
                      }
                    })
                  }
                })
                if (components.length) {
                  const messageBackups = [
                    {
                      content: `Retrieve data from component(s): ${components
                        .map(
                          (el) =>
                            `<p style="display: flex;align-items: center;">${el.code}<svg style="margin-left:8px;margin-right:8px;" xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4" fill="none"><circle cx="2" cy="2" r="2" fill="#7A7A7A"/></svg>${el.version}</p>`
                        )
                        .join("")}`,
                      type: MessageBackupType.RETRIEVE_BACKUP_VERSION,
                    },
                    {
                      content: "Decrypting data context and files",
                      type: MessageBackupType.DECRYPTING_BACKUP_VERSION,
                    },
                    {
                      content: "Encrypting data context and files",
                      type: MessageBackupType.ENCRYPTING_BACKUP_VERSION,
                    },
                    {
                      content: "Creating share version",
                      type: MessageBackupType.CREATING_BACKUP_FILE_VERSION,
                    },
                    {
                      content: "Sync version to invitees",
                      type: MessageBackupType.DONE_BACKUP_VERSION,
                    },
                  ]
                  setMessageBackups(messageBackups)
                  errorMessageBackup.setValue(false)
                  setCurrentMessageBackup(
                    MessageBackupType.RETRIEVE_BACKUP_VERSION
                  )
                  isLoadingBackupInvitee.setValue(true)
                  setTimeout(() => {
                    setCurrentMessageBackup(
                      MessageBackupType.DECRYPTING_BACKUP_VERSION
                    )
                  }, 200)
                  for (const elComponent of components) {
                    const { dataComponentRequest } =
                      await createNewComponentHistoryByOwner(
                        projectBuild.project_id,
                        elComponent.type,
                        elComponent.project_component_history_id,
                        elComponent.conversation_id
                      )
                    await syncNewVersionComponentInviteeMiddleWare(
                      dataComponentRequest,
                      [],
                      elComponent.project_component_history_id,
                      projectBuild.id,
                      []
                    )
                    setCurrentMessageBackup(
                      MessageBackupType.ENCRYPTING_BACKUP_VERSION
                    )
                  }
                  const dataInvitees = await getProjectBuildInviteeMiddleware(
                    projectBuild.id
                  )
                  newInvitees = dataInvitees
                  setCurrentMessageBackup(
                    MessageBackupType.CREATING_BACKUP_FILE_VERSION
                  )
                  setCurrentMessageBackup(MessageBackupType.DONE_BACKUP_VERSION)
                }
                for (const elNewInvitee of newInvitees) {
                  if (elNewInvitee.status === BUILD_INVITEE_STATUS.DEACTIVATE) {
                    toast(
                      <LabelNotificationPage
                        messenger={`${elNewInvitee.conversation_code} is currently deactivated. To share the key, please activate it within the build.`}
                        type="error"
                      />
                    )
                    continue
                  }
                  const emails = [elNewInvitee.email].concat(
                    elNewInvitee.publicKeyCCs?.map((el) => el.email) || []
                  )
                  const shareKeyFn = handleShareKey(
                    compact(emails),
                    elNewInvitee,
                    false,
                    isSentMailAll
                  )
                  await shareKeyFn()
                }
                setCurrentMessageBackup(MessageBackupType.DONE_BACKUP_VERSION)
                getInvitees(projectBuild.id)
                setTimeout(() => {
                  isLoadingBackupInvitee.setValue(false)
                }, 2000)
              },
            },
          },
        })
      )
    }

  const renderCheckKey = (
    isHasKey: boolean,
    title: string,
    isDeactivate: boolean
  ) => {
    return (
      <div className="flex items-center">
        {isHasKey && !isDeactivate ? <IconHasKey /> : <IconNotHasKey />}
        <p
          className="font-normal text-[13px] leading-[22px] text-[#7A7A7A] ml-[4px]"
          style={{
            whiteSpace: "nowrap",
            color: isDeactivate ? "#C7C7C7" : "#111111",
          }}
        >
          {title}
        </p>
      </div>
    )
  }

  const renderInfoInvitee = (
    name: string,
    email: string,
    isBorderBottom: boolean,
    has_key: {
      build: boolean
      component: boolean
      conversation: boolean
      isSent: boolean
      accept: boolean
    },
    isDeactivate: boolean,
    originInvite: ProjectBuildInvitessDetail
  ) => {
    const textColor = isDeactivate ? "#C7C7C7" : "#111111"
    const dotColor = isDeactivate ? "#C7C7C7" : "#7A7A7A"

    const hasComponents = Boolean(originInvite.project_build_components.length)
    return (
      <div
        className="grid grid-cols-2 mt-[12px]"
        style={{
          borderBottom: isBorderBottom ? "1px solid #E4E4E4" : "none",
          paddingBottom: isBorderBottom ? 8 : 0,
        }}
      >
        <div className="flex items-center">
          <Avatar
            width="24px"
            height="24px"
            name={name || email}
            textcolor="white"
            contentsize="11px"
          />

          {name && (
            <>
              <p
                className="font-semibold text-[13px] ml-[12px] leading-[22px]"
                style={{ color: textColor }}
              >
                {name}
              </p>
              <div
                className="h-[4px] w-[4px] mx-2 rounded-full"
                style={{ backgroundColor: dotColor }}
              />
            </>
          )}

          <p
            className="font-normal text-[13px] leading-[22px]"
            style={{
              marginLeft: name ? 0 : 12,
              color: textColor,
            }}
          >
            {email}
          </p>
        </div>

        <div className="flex items-center justify-end gap-[12px]">
          <div className="flex gap-[12px]" style={{ flexWrap: "wrap" }}>
            {renderCheckKey(
              has_key.conversation,
              "Conversation key",
              isDeactivate
            )}
            {renderCheckKey(has_key.build, "Build key", isDeactivate)}
            {renderCheckKey(has_key.isSent, "Email Sent", isDeactivate)}
            {renderCheckKey(has_key.accept, "Accept", isDeactivate)}

            {hasComponents ? (
              renderCheckKey(has_key.component, "Component key", isDeactivate)
            ) : (
              <div className="min-w-[117px]"></div>
            )}
          </div>

          <Button
            title="Share key"
            sizeBtn="small"
            colorBtn="white"
            isDisabledBtn
            disabled={isDeactivate}
            styleButton={{ minWidth: 100 }}
            onClick={handleShareKey([email], originInvite)}
          />
        </div>
      </div>
    )
  }

  const renderNoData = (title: string = "") => (
    <div className="h-full w-full flex flex-col items-center justify-center">
      <img src={iconNoData} alt="icon" style={{ height: 168 }} />
      {title ? (
        <Typography
          textClass="font-base text-sm py-6"
          style={{
            color: "#111111",
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
      ) : null}
    </div>
  )

  const renderEncryptedWarning = (
    type: "project" | "build",
    name: string,
    id: string,
    conversationCodes?: string[]
  ) => (
    <div className="h-full w-full flex items-center justify-center">
      <EncryptedDataWarning
        type={type}
        isOwner={true}
        name={name}
        id={id}
        conversationCodes={conversationCodes}
        isEncrypted={projectBuild.is_encrypted_project || false}
      />
    </div>
  )

  const renderHeader = () => {
    const newInvitees = cloneDeep(invitess).filter(
      (el) => el.status === BUILD_INVITEE_STATUS.ACTIVATE
    )
    const isShowAll = Boolean(newInvitees.length)
    return (
      <div className="min-h-[74px] border-b border-[#E4E4E4] flex items-center justify-between px-[24px]">
        <div className="flex flex-col">
          <p className="font-semibold text-[16px] text-[#111111]">
            {projectBuild.name}
          </p>
          <div className="flex items-center">
            <p className="text-[12px] font-semibold text-[#111111] whitespace-nowrap">
              {projectBuild.code}
            </p>
            <DotCard styleRoot={{ marginLeft: 4, marginRight: 4 }} />
            <LabelStatusBuild
              status={projectBuild.status ?? STATUS_BUILD.DRAFT}
              rootStyle={{ fontSize: 12, lineHeight: "18px" }}
            />
          </div>
        </div>
        <div className="flex items-center gap-[8px]">
          {/* <Button
            title="Share all invitations and keys"
            sizeBtn="small"
            colorBtn="yellow"
            isDisabledBtn
            disabled={!isShowAll}
            onClick={handleShareAllKeyForBuild(cloneDeep(invitess), true)}
            heightBtn={35}
            widthBtn={200}
          /> */}
          <Button
            title="Share all keys"
            sizeBtn="small"
            colorBtn="yellow"
            isDisabledBtn
            disabled={!isShowAll}
            onClick={handleShareAllKeyForBuild(cloneDeep(invitess), false)}
            heightBtn={35}
            widthBtn={120}
          />
        </div>
      </div>
    )
  }

  const renderComponentChip = (component, isDeactivate) => (
    <div
      key={`${component.code}-${component.version}`}
      className={`h-7 flex w-fit bg-grayWhite2 border border-[#E4E4E4] rounded-[4px] ${
        isDeactivate ? "bg-grayWhite" : ""
      }`}
    >
      <div className="h-7 flex items-center px-2 border-r border-[#E4E4E4]">
        <p
          className="font-medium text-[12px] leading-[18px]"
          style={{ color: isDeactivate ? "#C7C7C7" : "#111111" }}
        >
          {component.type_key}
        </p>
      </div>
      <div className="h-7 flex items-center px-2">
        <p
          className="font-medium text-[12px] leading-[18px] text-ellipsis overflow-hidden"
          style={{ color: isDeactivate ? "#C7C7C7" : "#111111" }}
        >
          {component.code}
        </p>
        <DotCard isDisabled={isDeactivate} />
        <p
          className="font-medium text-[12px] leading-[18px]"
          style={{ color: isDeactivate ? "#C7C7C7" : "#111111" }}
        >
          {component.version}
        </p>
      </div>
    </div>
  )

  const renderInviteeBlock = (invitee: ProjectBuildInvitessDetail) => {
    const isDeactivate = invitee.status === BUILD_INVITEE_STATUS.DEACTIVATE

    return (
      <div
        key={invitee.email}
        className="flex flex-col bg-white pt-3 pb-3 border border-[#E4E4E4] rounded-[6px]"
      >
        <div className="flex flex-col pb-3 px-3 gap-1 border-b border-[#E4E4E4]">
          <div className="flex justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-[12px] min-h-[37px]">
                <p
                  className="font-semibold text-[14px] leading-[22px]"
                  style={{ color: isDeactivate ? "#C7C7C7" : "#111111" }}
                >
                  {invitee.conversation_code}
                </p>
                {isDeactivate ? (
                  <div className="p-[8px] bg-[#ea454526] rounded-[4px]">
                    <p
                      className="text-[#EA4545] text-[14px]"
                      style={{
                        fontWeight: 600,
                      }}
                    >
                      DEACTIVATE
                    </p>
                  </div>
                ) : null}
              </div>

              <div className="flex items-center">
                <p
                  className="font-semibold max-w-[461px] overflow-hidden text-ellipsis text-[13px] leading-[22px]"
                  style={{ color: isDeactivate ? "#C7C7C7" : "#111111" }}
                >
                  {invitee.name}
                </p>
                <div
                  className="hidden md:block h-1 w-1 mx-2 rounded-full"
                  style={{
                    backgroundColor: isDeactivate ? "#C7C7C7" : "#7A7A7A",
                  }}
                />
                <p
                  className="font-normal text-[13px] leading-[22px]"
                  style={{ color: isDeactivate ? "#C7C7C7" : "#7A7A7A" }}
                >
                  {invitee.email}
                </p>
              </div>
            </div>
            <Button
              title="Share all"
              sizeBtn="small"
              colorBtn="yellow"
              isDisabledBtn
              disabled={isDeactivate}
              heightBtn={35}
              widthBtn={100}
              onClick={handleWarningShareAllKey(
                compact(
                  [invitee.email].concat(
                    invitee.publicKeyCCs?.map((el) => el.email) || []
                  )
                ),
                invitee
              )}
            />
          </div>

          <div className="flex items-center mt-[12px] flex-wrap gap-2">
            {invitee.project_build_components?.map((component) =>
              renderComponentChip(component, isDeactivate)
            )}
          </div>
        </div>

        <div className="flex flex-col px-3">
          {renderInfoInvitee(
            invitee.name,
            invitee.email,
            Boolean(invitee.publicKeyCCs?.length),
            {
              ...(invitee.has_key_invitee || {
                build: false,
                component: false,
                conversation: false,
              }),
              isSent: invitee.is_sent_invitee || false,
              accept: invitee.accepted || false,
            },
            isDeactivate,
            invitee
          )}
          {invitee.publicKeyCCs?.map((elCC, index) =>
            renderInfoInvitee(
              "",
              elCC.email,
              index + 1 !== invitee.publicKeyCCs?.length,
              {
                ...(elCC.has_key || {
                  build: false,
                  component: false,
                  conversation: false,
                }),
                isSent: elCC.is_sent || false,
                accept: elCC.accepted || false,
              },
              isDeactivate,
              invitee
            )
          )}
        </div>
      </div>
    )
  }

  const renderComponent = () => {
    return (
      <div className="flex flex-col gap-[8px]">
        <p
          className="text-black"
          style={{
            fontWeight: 600,
          }}
        >{`Components`}</p>
        <div
          className="flex items-center gap-[12px]"
          style={{
            flexWrap: "wrap",
          }}
        >
          {projectBuild.components
            .filter((el) => !el.is_build_component_archived)
            .map((elComponent, index) => {
              return (
                <div
                  key={index}
                  className={`flex flex-col w-[261px] py-[9px] px-3 gap-2 relative border-stroke border-[1px] border-solid rounded-[6px]`}
                >
                  <div className="flex">
                    <div className="h-7 flex items-center ">
                      <p
                        className="font-medium px-1 py-[2px] rounded-4"
                        style={{
                          fontSize: 12,
                          lineHeight: "18px",
                          color: "#FFFFFF",
                          backgroundColor: "#333333",
                        }}
                      >
                        {elComponent.type.key}
                      </p>
                    </div>
                    <div
                      className="h-7 flex items-center px-2"
                      style={{
                        color: "#111111",
                      }}
                    >
                      <p
                        className="font-medium"
                        style={{
                          fontSize: 12,
                          lineHeight: "18px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {elComponent.code}
                      </p>
                      <DotCard styleRoot={{ margin: 4 }} />
                      <p
                        className="font-medium"
                        style={{
                          fontSize: 12,
                          lineHeight: "18px",
                        }}
                      >
                        {elComponent.version}
                      </p>
                    </div>
                  </div>

                  <p
                    className="font-medium text-black text-sm overflow-hidden text-ellipsis "
                    style={{
                      color: "#111111",
                    }}
                  >
                    {elComponent.name}
                  </p>
                </div>
              )
            })}
        </div>
      </div>
    )
  }

  const renderLayout = () => {
    if (loadingData.value) {
      return <SkeletonProjectBuildDetail />
    }
    if (!projectBuild.id || !projectBuildIdActive.value) return renderNoData()

    const projectKey = projectEncryptionKeys[projectBuild.project_id]
    if (!projectKey) {
      return renderEncryptedWarning(
        "project",
        `"${projectBuild.project_name}"`,
        projectBuild.project_id
      )
    }

    const isBuildKeyValid =
      projectBuild.decryptedBuildKey &&
      typeof projectBuild.decryptedBuildKey === "string" &&
      projectBuild.decryptedBuildKey.length === 64 &&
      projectBuild.decryptedBuildKey !== projectBuild?.buildKey?.encryptedKey

    if (!isBuildKeyValid || !currentProjectBuildEncryptionKey) {
      return renderEncryptedWarning(
        "build",
        `ID: ${projectBuild.code}`,
        projectBuild.id
      )
    }

    const notDecryptedConversationKeys =
      projectBuild?.notDecryptedConversationKeys ?? []

    if (notDecryptedConversationKeys.length > 0) {
      const conversationCodes = notDecryptedConversationKeys.map(
        (el) => el.conversationCode
      )
      return renderEncryptedWarning(
        "build",
        `in build ID: ${projectBuild.code}`,
        projectBuild.id,
        conversationCodes
      )
    }

    return (
      <div className="flex flex-col h-full w-full">
        {renderHeader()}
        <div className="flex flex-col flex-1 overflow-auto p-[24px] gap-[12px]">
          {projectBuild.components.filter(
            (el) => !el.is_build_component_archived
          )?.length
            ? renderComponent()
            : null}
          <p
            className="text-black"
            style={{
              fontWeight: 600,
            }}
          >{`Invitees`}</p>
          {invitess.length
            ? invitess.map(renderInviteeBlock)
            : renderNoData("No invitees found.")}
        </div>
        {isLoadingBackupInvitee.value && messageBackups.length ? (
          <FormBackup
            messageBackups={messageBackups}
            currentBackup={currentMessageBackup}
            errorMessageBackup={errorMessageBackup.value}
          />
        ) : null}
      </div>
    )
  }
  return renderLayout()
}
